/**
 * The one place the toast rule is stated, and the one place it is applied.
 *
 * OWNER RULE (approved 2026-08-26), verbatim: "A toast appears exactly when an
 * author-initiated action has been committed by the server and succeeded;
 * nothing else ever toasts."
 *
 * So a toast is a statement about the SERVER, not about the UI. It fires from a
 * mutation's success path and from nowhere else:
 *
 *   - NOT on navigation, tab switches, or edits before a save — nothing was
 *     committed;
 *   - NOT on opening or cancelling a dialog — a dialog confirms INTENT before
 *     the fact, a toast reports the RESULT after it, and a dialog closing is no
 *     evidence anything landed;
 *   - NOT on any failure. Errors stay inline, beside the control that failed
 *     and beside the retry. A toast that auto-dismisses is the wrong carrier
 *     for something an author has to act on.
 *
 * A RECONCILED result IS a success. When a publish landed but its response
 * failed, the committed fact is identical to the plain case, so it gets the
 * SAME string. Describing the mechanism's stumble instead would tell the author
 * about our plumbing rather than about their content.
 *
 * ## Why the message is a NODE and not a string
 *
 * This lives among the mutation hooks, and that layer imports no i18n — the
 * hooks know about the wire and the cache, not about words. Resolving the text
 * here would have made every mutation hook depend on the language port, and
 * every test that renders one mount it, to test cache invalidation.
 *
 * So the announcer hands the toast a node that translates ITSELF, in the
 * Toaster's own tree, where UI concerns already live. Portals keep React
 * context, so the lookup resolves exactly as it would inline. The boundary
 * stays where it was and the rule still has one home.
 */

import type { JSX } from 'react';
import type { MessageKeys } from '../../../i18n/messages/index.js';
import { useTranslate } from '../../../i18n/useTranslate.js';
import { toast } from '../../../primitives/Toaster.js';

/** Resolves the message where the toast renders, not where it is fired. */
function CommittedMessage({ messageKey }: { readonly messageKey: MessageKeys }): JSX.Element {
  const t = useTranslate();
  return <>{t(messageKey)}</>;
}

/**
 * Returns the announcer for a committed lifecycle action. Takes the message KEY
 * rather than the message, and the key type is the message catalogue's own
 * union — so a
 * call site cannot pass a coined sentence, and a key that does not exist does
 * not compile. Every toast in this set is one of the decided strings.
 */
export function useCommittedToast(): (messageKey: MessageKeys) => void {
  // Not memoised: nothing depends on this function's identity — it is called
  // inside a mutation callback, never passed as a prop or a dependency — so a
  // manual memo here would buy nothing and owe an exception marker.
  return (messageKey: MessageKeys) => {
    toast.success(<CommittedMessage messageKey={messageKey} />);
  };
}
